import { identity as identityClient, unwrap, ApiError } from "../api";
import { isGuid, redirectToCanonical, userPath } from "../paths";

/**
 * Staff/company user administration (contracts/admin-users.md's "Administering users" section,
 * `UserAdminController.cs`). A sibling of `lib/identity.ts`, not an extension of it: that file is
 * the caller acting on their OWN account (sign-in, `/me`, `/me/sessions`); this one is an
 * administrator acting on SOMEONE ELSE's, the same split `UserAdminController.cs`'s own remarks
 * draw against `MeController`. Matches `lib/services/customers.ts`'s shape — one file per admin
 * domain, calling the generated `identity()` client, never a hand-rolled fetch.
 *
 * Every route here requires `company.user.administer` (verified against each endpoint's own
 * `[Authorize]` attribute, not assumed from the contract doc — see this file's own note below on
 * where the doc and the live controller disagree).
 *
 * GAP-4 applies here exactly as it does to `lib/identity.ts`: Identity declares no response body
 * on any operation, so every generated route types its response `undefined` and every shape below
 * is asserted at runtime, not hand-cast.
 */

export type UserSummary = {
  id: string;
  /** `USR-000042` — the account's URL key (`userPath`); not personal data. Null only if Identity omits it. */
  userNumber: string | null;
  displayName: string | null;
  email: string | null;
  /** An email given without proving it (a mobile-registered customer's profile email) — never a sign-in key. */
  contactEmail: string | null;
  mobile: string | null;
  /** A mobile given without proving it (an email-registered account's profile number) — never a sign-in key. */
  contactMobile: string | null;
  role: string;
  companyRef: string | null;
  /** Customers' name for `companyRef`; null for staff or when unresolved. */
  companyName: string | null;
  /** `AccountStatus.cs`'s own lower-cased name on the wire: "pending" | "active" | "deactivated" | "erased". */
  status: string;
  /** Computed by Identity from the lockout row, not stored (FR-001a) — reported beside `status`, never folded into it. */
  locked: boolean;
  lockedUntil: string | null;
  /** Set only for a self-registered person: true = said they were a firm. Null for invited and seeded accounts. */
  registeredAsCompany: boolean | null;
};

/**
 * What an account with no `companyRef` is: Infra Junction staff, unless the person registered
 * themselves and has not given their customer details yet (`registeredAsCompany` set) — then it is
 * a sign-up waiting on its profile, not a back-office account.
 */
export function noCompanyLabel(user: Pick<UserSummary, "registeredAsCompany">): string {
  if (user.registeredAsCompany === true) return "Signed up as a company — details not given yet";
  if (user.registeredAsCompany === false) return "Signed up — profile not completed";
  return "Infra Junction staff";
}

export type PagedUsers = { items: UserSummary[]; page: number; pageSize: number; totalCount: number };

function text(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function parseUser(raw: unknown): UserSummary {
  const row = (raw ?? {}) as Record<string, unknown>;
  const id = text(row.id);
  const role = text(row.role);
  const status = text(row.status);
  if (id === null || role === null || status === null) {
    throw new Error("The API answered a user without a string id, role and status. There is nothing usable to render.");
  }
  return {
    id,
    userNumber: text(row.userNumber),
    displayName: text(row.displayName),
    email: text(row.email),
    contactEmail: text(row.contactEmail),
    mobile: text(row.mobile),
    contactMobile: text(row.contactMobile),
    role,
    companyRef: text(row.companyRef),
    companyName: text(row.companyName),
    status,
    locked: row.locked === true,
    lockedUntil: text(row.lockedUntil),
    registeredAsCompany: typeof row.registeredAsCompany === "boolean" ? row.registeredAsCompany : null,
  };
}

/**
 * `GET /api/identity/users`. Paged and scoped server-side (a Company Admin's caller-scope is
 * applied before the query runs, not filtered after — `ListUsersQuery`'s own remark).
 *
 * `?status=&role=&q=` (`admin-users.md`) narrow it inside that scope, and `totalCount` counts the
 * narrowed set. `?permission=` keeps the roles the live matrix grants that code (not `*`), so a
 * picker can ask "who packs" without naming a role. `status` is the wire name (`pending`, `active`, …); an unknown one is a `400`.
 *
 * The response carries `items` + `totalCount` only (`UserPage` has no `Page`/`PageSize` field to
 * echo back) — `page`/`pageSize` below are the values this call requested, not read off the body.
 */
export async function listUsers(params: {
  page: number;
  pageSize?: number;
  q?: string;
  role?: string;
  status?: string;
  permission?: string;
}): Promise<PagedUsers> {
  const pageSize = params.pageSize ?? 50;
  const data = await unwrap<unknown>(
    (await identityClient()).GET("/api/identity/users", {
      params: { query: { page: params.page, pageSize, q: params.q, role: params.role, status: params.status, permission: params.permission } },
    }),
  );
  const row = (data ?? {}) as Record<string, unknown>;
  const items = Array.isArray(row.items) ? row.items.map(parseUser) : [];
  const totalCount = typeof row.totalCount === "number" ? row.totalCount : items.length;
  return { items, page: params.page, pageSize, totalCount };
}

/**
 * `GET /api/identity/users/packers` — active accounts with the `packer` role, nothing else. Needs
 * `packing.oversight` (a supervisor), not `user.status`; the picker on the packing screen reads it.
 */
export async function listPackers(q?: string): Promise<PagedUsers> {
  const data = await unwrap<unknown>((await identityClient()).GET("/api/identity/users/packers", { params: { query: { q } } }));
  const row = (data ?? {}) as Record<string, unknown>;
  const items = Array.isArray(row.items) ? row.items.map(parseUser) : [];
  return { items, page: 1, pageSize: items.length, totalCount: typeof row.totalCount === "number" ? row.totalCount : items.length };
}

/**
 * `GET /api/identity/users/dispatchers` — active accounts whose role the live matrix grants
 * `dispatch.execute`. Needs `dispatch.assign`.
 */
export async function listDispatchers(q?: string): Promise<UserSummary[]> {
  const data = await unwrap<unknown>((await identityClient()).GET("/api/identity/users/dispatchers", { params: { query: { q } } }));
  const row = (data ?? {}) as Record<string, unknown>;
  return Array.isArray(row.items) ? row.items.map(parseUser) : [];
}

/** `GET /api/identity/users/{id}`. `null` for out-of-scope or missing — the route answers 404 either way (disclosure rule). */
export async function getUser(id: string): Promise<UserSummary | null> {
  try {
    return parseUser(await unwrap<unknown>((await identityClient()).GET("/api/identity/users/{id}", { params: { path: { id } } })));
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** `GET /api/identity/users/by-number/{userNumber}` — the same body, scope and `null`-on-404 as `getUser`. */
export async function getUserByNumber(userNumber: string): Promise<UserSummary | null> {
  try {
    return parseUser(
      await unwrap<unknown>((await identityClient()).GET("/api/identity/users/by-number/{userNumber}", { params: { path: { userNumber } } })),
    );
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** The account a route's `:userNumber` names. A legacy id throws a redirect to the number URL, query kept. */
export async function userByKey(request: Request, key: string): Promise<UserSummary | null> {
  if (!isGuid(key)) return getUserByNumber(key);
  const user = await getUser(key);
  if (!user?.userNumber) return user;
  throw redirectToCanonical(request, key, userPath(user.userNumber));
}

/**
 * `PUT /api/identity/users/{id}/role`. `204`, and every session the account holds is revoked
 * server-side in the same transaction (FR-038) — nothing to do here for that.
 *
 * `role` is still free text on the wire (`ChangeRoleRequest.role: string?`, no enum) — the caller
 * now picks it from a `<select>` populated by `lib/identity.ts`'s `listGrantableRoles`
 * (`GET /api/identity/me/grantable-roles`, no special grant required), but this call still
 * independently enforces the same matrix check server-side (422 unknown role, 403 not grantable):
 * the dropdown is a UI hint, not a trust boundary, and a stale list or a tampered request still
 * gets refused here.
 */
export async function changeUserRole(id: string, role: string): Promise<void> {
  await unwrap((await identityClient()).PUT("/api/identity/users/{id}/role", { params: { path: { id } }, body: { role } }));
}

/** `POST /api/identity/users/{id}/deactivate`. `204`; all sessions revoked. Reversible. */
export async function deactivateUser(id: string): Promise<void> {
  await unwrap((await identityClient()).POST("/api/identity/users/{id}/deactivate", { params: { path: { id } } }));
}

/** `POST /api/identity/users/{id}/reactivate`. `204`. No session is restored — revocation does not un-happen. */
export async function reactivateUser(id: string): Promise<void> {
  await unwrap((await identityClient()).POST("/api/identity/users/{id}/reactivate", { params: { path: { id } } }));
}

/** `POST /api/identity/users/{id}/unlock`. `204`; idempotent. */
export async function unlockUser(id: string): Promise<void> {
  await unwrap((await identityClient()).POST("/api/identity/users/{id}/unlock", { params: { path: { id } } }));
}

/** Identity's `pending` is an invitation not yet accepted; it reads as "invited" everywhere it is shown. */
export function userStatusLabel(status: string): string {
  return status === "pending" ? "invited" : status;
}

/** `DELETE /api/identity/users/{id}/sessions`. `204`. Effective within one access-token lifetime, not immediately. */
export async function revokeUserSessions(id: string): Promise<void> {
  await unwrap((await identityClient()).DELETE("/api/identity/users/{id}/sessions", { params: { path: { id } } }));
}
